import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Trash2, Copy, Check, RefreshCw } from 'lucide-react';
import { useStore } from '../../store/StoreContext';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { FormSection } from '../../components/ui/FormSection';
import { TextField, TextArea } from '../../components/ui/TextField';
import { Toggle } from '../../components/ui/Toggle';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Toast } from '../../components/ui/Toast';
import { UpgradePrompt } from '../../components/UpgradePrompt';
import { SystemDiscoverEditor, type SystemSpecPatch } from '../../components/integrations/SystemDiscoverEditor';
import { uid, type Mapping } from '../../types';
import { isSampleFormat, parseSample, previewMapping, samplePlaceholder, type PreviewRecordResult } from '../../lib/mappingPreview';
import type { MappingSpec } from '../../lib/mappingEngine';
import { LimitError, fetchMapping } from '../../api/dataApi';

// A brand-new mapping's local draft. nickname is required + immutable
// once created (it's the key), so it's only editable on this screen in
// create mode.
function emptyDraft(): Mapping {
  return {
    id: uid(),
    nickname: '',
    title: '',
    description: '',
    isPublic: false,
    sourceType: '',
    spec: {},
  };
}

function prettySpec(spec: Record<string, unknown>): string {
  try {
    return JSON.stringify(spec ?? {}, null, 2);
  } catch {
    return '{}';
  }
}

export function MappingManageScreen() {
  const { mappingId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Reused, unmodified editor - only the destinations it navigates back to
  // depend on which facade opened it (roadmap #9 Phase 1 slice 4: reached
  // both from /mappings/:id/edit directly and from the Integrations detail
  // shell's /integrations/:id/mapping). See docs/INTEGRATION_TOOL_PHASE0_AUDIT.md's
  // file table: "Retained as the Advanced raw-JSON editor ... Minimal edits."
  const isIntegrationsRoute = location.pathname.startsWith('/integrations/');
  const listUrl = isIntegrationsRoute ? '/integrations' : '/mappings';
  const detailUrl = (id: string) => (isIntegrationsRoute ? `/integrations/${id}` : `/mappings/${id}`);
  const editUrl = (id: string) => (isIntegrationsRoute ? `/integrations/${id}/mapping` : `/mappings/${id}/edit`);
  const {
    mappings,
    addMapping,
    updateMapping,
    deleteMapping,
    rotateMappingIngestToken,
    rotateMappingWebhookSecret,
    isMappingOwner,
  } = useStore();

  const local = mappingId ? mappings?.find((m) => m.id === mappingId) : undefined;
  // Fallback for when this mapping isn't in the client-side `mappings` cache
  // yet (a fresh page load or direct navigation to this route before
  // syncMappings has run) - without this, `local` comes back undefined and
  // the screen would silently render in create mode instead of edit mode,
  // same bug class IntegrationDetailScreen's own `fetched` fallback already
  // guards against for its own read-only view.
  const [fetched, setFetched] = useState<Mapping | null>(null);
  useEffect(() => {
    if (local || !mappingId) return;
    let cancelled = false;
    fetchMapping(mappingId).then((m) => {
      if (!cancelled && m) setFetched(m);
    });
    return () => {
      cancelled = true;
    };
  }, [local, mappingId]);

  const existing = local ?? fetched ?? undefined;
  const isEdit = !!mappingId;

  const [draft, setDraft] = useState<Mapping>(() => existing ?? emptyDraft());
  const [specText, setSpecText] = useState<string>(() => prettySpec((existing ?? draft).spec));
  // The draft/specText above are seeded once at mount from whatever
  // `existing` happened to be at that instant - if it was still undefined
  // (the fallback fetch above hadn't resolved yet), this reconciles them
  // once it does, without clobbering edits the user already started typing.
  const [hydrated, setHydrated] = useState(!!existing);
  useEffect(() => {
    if (hydrated || !existing) return;
    setDraft(existing);
    setSpecText(prettySpec(existing.spec));
    setHydrated(true);
  }, [hydrated, existing]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [limitMessage, setLimitMessage] = useState('');
  // The raw ingest token, shown exactly once right after a mint/rotate
  // (see Mapping.ingestToken's comment) - kept only in this screen's local
  // state, never re-derived from `draft`, so navigating away and back (or
  // the next save) makes it disappear the same way it does server-side.
  const [revealedToken, setRevealedToken] = useState('');
  const [tokenCopied, setTokenCopied] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [rotatingSecret, setRotatingSecret] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);
  const [sampleText, setSampleText] = useState('');
  const [previewResults, setPreviewResults] = useState<PreviewRecordResult[] | null>(null);
  const [previewError, setPreviewError] = useState('');

  const set = <K extends keyof Mapping>(key: K, val: Mapping[K]) => setDraft((d) => ({ ...d, [key]: val }));

  const specError = useMemo(() => {
    if (specText.trim() === '') return '';
    try {
      const parsed = JSON.parse(specText);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        return 'Spec must be a JSON object.';
      }
      return '';
    } catch {
      return 'Spec is not valid JSON.';
    }
  }, [specText]);

  // An edit screen the caller doesn't own - bounce to the read-only view.
  if (isEdit && existing && !isMappingOwner(existing)) {
    navigate(detailUrl(existing.id), { replace: true });
    return null;
  }

  const canSave =
    draft.title.trim() !== '' && (isEdit || draft.nickname.trim() !== '') && specError === '' && !saving;

  // Only json/csv mappings run through mapping_engine.c's rule engine -
  // Prometheus's mapping is fixed (metric -> Signal, no rules/outputs) and
  // has nothing for this preview to apply. See mappingEngine.ts's header
  // comment for why this runs entirely in the browser.
  const normalizedSourceType = draft.sourceType.trim().toLowerCase();
  const previewable = isSampleFormat(normalizedSourceType);
  const isConnector = normalizedSourceType === 'webhook';
  // Live discovery only applies to an already-saved "system" mapping - it
  // needs a real nickname/channel for a connected zagentc daemon to answer
  // into (see SystemDiscoverEditor's own header comment), so never during
  // creation.
  const isSystem = isEdit && normalizedSourceType === 'system';

  // Best-effort read of spec.provider straight out of the raw JSON editor
  // (not draft.spec, which only updates on save) - just to decide which
  // auth panel below to show; an invalid/absent value falls back to the
  // "generic" bearer-token panel, which is also what the backend itself
  // defaults an unset spec.provider to (see internal/webhookrecv's
  // authenticate). Plain computation, not a hook - it's a single cheap
  // JSON.parse of an already-in-memory string, nothing to memoize, and
  // the early "not owner" return above rules out useMemo here anyway
  // (hooks can't follow a conditional return).
  const provider = (() => {
    if (!isConnector) return 'generic';
    try {
      const parsed = specText.trim() === '' ? {} : JSON.parse(specText);
      const p = typeof parsed?.provider === 'string' ? parsed.provider.trim().toLowerCase() : '';
      return p || 'generic';
    } catch {
      return 'generic';
    }
  })();

  // Same tolerant-parse approach as `provider` above, for SystemDiscoverEditor's
  // own three array fields. An unparsed/non-array value degrades to "no
  // filter" (empty array) rather than throwing - the raw JSON editor is
  // still the source of truth and can hold something this picker doesn't
  // understand; this just reads what it can.
  const systemSpecArray = (key: string): string[] => {
    try {
      const parsed = specText.trim() === '' ? {} : JSON.parse(specText);
      const v = parsed?.[key];
      return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
    } catch {
      return [];
    }
  };
  const applySystemSpecPatch = (patch: SystemSpecPatch) => {
    let parsed: Record<string, unknown>;
    try {
      parsed = specText.trim() === '' ? {} : JSON.parse(specText);
    } catch {
      return; // the raw JSON is currently broken - nothing sensible to patch
    }
    const next = { ...parsed, ...patch };
    for (const key of ['dfMounts', 'netInterfaces', 'netExclude'] as const) {
      if (Array.isArray(next[key]) && next[key].length === 0) delete next[key];
    }
    setSpecText(prettySpec(next));
  };

  const runPreview = () => {
    setPreviewError('');
    setPreviewResults(null);
    if (specError) {
      setPreviewError('Fix the spec above before previewing.');
      return;
    }
    try {
      const spec = (specText.trim() === '' ? {} : JSON.parse(specText)) as MappingSpec;
      const records = parseSample(normalizedSourceType, sampleText, spec.recordPath);
      if (records.length === 0) {
        setPreviewError(
          normalizedSourceType === 'csv' || normalizedSourceType === 'tsv'
            ? 'Paste a header row plus at least one data row.'
            : 'Paste a sample with at least one record (matching spec.recordPath if set).',
        );
        return;
      }
      setPreviewResults(previewMapping(spec, records, draft.nickname || 'sample'));
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'Could not parse that sample.');
    }
  };

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const spec = specText.trim() === '' ? {} : JSON.parse(specText);
      const payload: Mapping = {
        ...draft,
        nickname: isEdit ? draft.nickname : draft.nickname.trim(),
        title: draft.title.trim(),
        sourceType: draft.sourceType.trim(),
        spec,
      };
      const saved = isEdit ? await updateMapping(payload) : await addMapping(payload);
      navigate(editUrl(saved.id), { replace: true });
      setDraft(saved);
      setSpecText(prettySpec(saved.spec));
      // A brand-new mapping mints a token; an update never does (see
      // Mapping.ingestToken's comment) - so this only fires on create.
      if (saved.ingestToken) {
        setRevealedToken(saved.ingestToken);
        setTokenCopied(false);
      }
    } catch (err) {
      // {code:"connector_limit"} - a fresh webhook connector past the
      // caller's tier cap (see backend/workflows/mapping/create.wsl).
      // Only create can hit this - editing an existing (already-over-cap)
      // connector is never blocked, see CheckConnectorQuota's comment.
      if (err instanceof LimitError) {
        setLimitMessage(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Unable to save this mapping.');
      }
    } finally {
      setSaving(false);
    }
  };

  const rotateToken = async () => {
    if (!existing) return;
    if (
      existing.ingestTokenSet &&
      !window.confirm('Rotate the ingest token? Anything still using the old one (a Prometheus remote_write config) will stop working.')
    ) {
      return;
    }
    setRotating(true);
    setError('');
    try {
      const saved = await rotateMappingIngestToken(existing.nickname);
      setDraft(saved);
      if (saved.ingestToken) {
        setRevealedToken(saved.ingestToken);
        setTokenCopied(false);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to rotate the ingest token.');
    } finally {
      setRotating(false);
    }
  };

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(revealedToken);
      setTokenCopied(true);
    } catch {
      // Clipboard access denied/unavailable - the token is still on
      // screen to select and copy by hand.
    }
  };

  const rotateSecret = async () => {
    if (!existing) return;
    if (
      existing.webhookSecret &&
      !window.confirm('Rotate the webhook secret? Update it in GitHub (or your other provider) too, or deliveries will start failing signature verification.')
    ) {
      return;
    }
    setRotatingSecret(true);
    setError('');
    try {
      const saved = await rotateMappingWebhookSecret(existing.nickname);
      setDraft(saved);
      setSecretCopied(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to rotate the webhook secret.');
    } finally {
      setRotatingSecret(false);
    }
  };

  const copySecret = async () => {
    if (!draft.webhookSecret) return;
    try {
      await navigator.clipboard.writeText(draft.webhookSecret);
      setSecretCopied(true);
    } catch {
      // Clipboard access denied/unavailable - still on screen to copy by hand.
    }
  };

  const remove = async () => {
    if (!existing) return;
    if (!window.confirm(`Delete "${existing.title || existing.nickname}"? This can't be undone.`)) return;
    setDeleting(true);
    try {
      await deleteMapping(existing.nickname);
      navigate(listUrl, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to delete this mapping.');
      setDeleting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <ScreenHeader title={isEdit ? 'Edit mapping' : 'New mapping'} backTo={listUrl} />

      {revealedToken && (
        <div className="rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4 flex flex-col gap-2">
          <p className="text-sm font-medium">
            Ingest token — copy it now. It won't be shown again; you'd have to rotate to get a new one.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 min-w-0 truncate rounded-lg bg-black/[0.06] dark:bg-white/10 px-3 py-2 text-[13px] font-mono select-all">
              {revealedToken}
            </code>
            <Button variant="secondary" onClick={copyToken}>
              {tokenCopied ? <Check size={16} /> : <Copy size={16} />}
              {tokenCopied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <button
            type="button"
            onClick={() => setRevealedToken('')}
            className="self-start text-xs text-black/50 dark:text-white/50 hover:underline"
          >
            Dismiss
          </button>
        </div>
      )}

      <FormSection title="Details">
        {!isEdit && (
          <TextField
            label="Nickname"
            placeholder="@github-deploys"
            value={draft.nickname}
            onChange={(e) => set('nickname', e.target.value)}
            className="font-mono"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
        )}
        {isEdit && (
          <div className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-black/70 dark:text-white/70">Nickname</span>
            <span className="font-mono text-[15px] text-black/50 dark:text-white/50">{draft.nickname}</span>
          </div>
        )}
        <TextField
          label="Title"
          placeholder="GitHub Actions deploys"
          value={draft.title}
          onChange={(e) => set('title', e.target.value)}
        />
        <TextArea
          label="Description"
          placeholder="What this mapping ingests and where it goes."
          value={draft.description}
          onChange={(e) => set('description', e.target.value)}
          className="min-h-[70px]"
        />
        <TextField
          label="Source type"
          placeholder="webhook, prometheus, google_sheets, csv…"
          value={draft.sourceType}
          onChange={(e) => set('sourceType', e.target.value)}
          className="font-mono"
        />
        <Toggle label="Public" checked={draft.isPublic} onChange={(v) => set('isPublic', v)} />
      </FormSection>

      {isEdit && existing && !(isConnector && (provider === 'github' || provider === 'stripe')) && (
        <FormSection title="Ingest token">
          <p className="text-xs text-black/45 dark:text-white/45 -mt-1">
            {isConnector
              ? 'Authenticates POST /ingest/webhook/' + existing.nickname + ' as Authorization: Bearer <token> (spec.provider "generic"). '
              : 'Authenticates Prometheus remote_write pushes to this mapping (Bearer token). '}
            Shown only once, when minted or rotated — this screen never displays an existing one.
          </p>
          <div className="flex items-center justify-between gap-3">
            <Badge>{existing.ingestTokenSet ? 'Configured' : 'Not configured'}</Badge>
            <Button variant="bordered" onClick={rotateToken} loading={rotating}>
              <RefreshCw size={15} />
              {existing.ingestTokenSet ? 'Rotate' : 'Generate'}
            </Button>
          </div>
        </FormSection>
      )}

      {isEdit && existing && isConnector && (provider === 'github' || provider === 'stripe') && (
        <FormSection title="Webhook secret">
          <p className="text-xs text-black/45 dark:text-white/45 -mt-1">
            {provider === 'stripe' ? (
              <>
                Verifies Stripe's <code>Stripe-Signature</code> header on deliveries to{' '}
                <code className="font-mono">POST /ingest/webhook/{existing.nickname}</code>. Paste this into your
                Stripe Dashboard's Webhooks → Signing secret field (Developers → Webhooks → your endpoint).
              </>
            ) : (
              <>
                Verifies GitHub's <code>X-Hub-Signature-256</code> on deliveries to{' '}
                <code className="font-mono">POST /ingest/webhook/{existing.nickname}</code>. Paste this into your
                repo's Settings → Webhooks → Secret field.
              </>
            )}{' '}
            Unlike the ingest token above, this stays visible here — you may need to copy it again later.
          </p>
          {draft.webhookSecret ? (
            <div className="flex items-center gap-2">
              <code className="flex-1 min-w-0 truncate rounded-lg bg-black/[0.06] dark:bg-white/10 px-3 py-2 text-[13px] font-mono select-all">
                {draft.webhookSecret}
              </code>
              <Button variant="secondary" onClick={copySecret}>
                {secretCopied ? <Check size={16} /> : <Copy size={16} />}
                {secretCopied ? 'Copied' : 'Copy'}
              </Button>
            </div>
          ) : (
            <Badge>Not configured</Badge>
          )}
          <Button variant="bordered" onClick={rotateSecret} loading={rotatingSecret}>
            <RefreshCw size={15} />
            Rotate
          </Button>
        </FormSection>
      )}

      <FormSection title="Spec">
        <p className="text-xs text-black/45 dark:text-white/45 -mt-1">
          {isConnector ? (
            <>
              A webhook connector needs <code>channel</code> (destination nickname) and, optionally,{' '}
              <code>provider</code>. <code>"github"</code> verifies GitHub's signature and turns workflow-run/
              deployment-status events into Signals with no further config; <code>"stripe"</code> verifies Stripe's
              signature and turns invoice.paid/checkout.session.completed/customer.subscription.deleted into
              revenue/new_customer/churn Signals with no further config; anything else (or no provider at all)
              accepts <code>Authorization: Bearer &lt;ingest token&gt;</code> and applies your own{' '}
              <code>rules</code>/<code>outputs</code> (see Preview below) to the posted JSON. Payload URL:{' '}
              <code className="font-mono">POST /ingest/webhook/{draft.nickname || '{nickname}'}</code>.
            </>
          ) : (
            'Opaque configuration the Integration Tool authors and a collector consumes. Stored as-is; must be a JSON object.'
          )}
        </p>
        {isSystem && (
          <SystemDiscoverEditor
            nickname={draft.nickname}
            collectors={systemSpecArray('collectors')}
            dfMounts={systemSpecArray('dfMounts')}
            netInterfaces={systemSpecArray('netInterfaces')}
            netExclude={systemSpecArray('netExclude')}
            onChange={applySystemSpecPatch}
          />
        )}
        <TextArea
          label="spec (JSON)"
          value={specText}
          onChange={(e) => setSpecText(e.target.value)}
          error={specError || undefined}
          className="font-mono text-[13px] min-h-[220px]"
          spellCheck={false}
        />
      </FormSection>

      {previewable && (
        <FormSection title="Preview">
          <p className="text-xs text-black/45 dark:text-white/45 -mt-1">
            Paste a sample ({normalizedSourceType.toUpperCase()}) to see the
            Signals this spec would produce. Runs entirely in your browser - nothing here is sent anywhere.
          </p>
          <TextArea
            label={`Sample ${normalizedSourceType.toUpperCase()}`}
            placeholder={samplePlaceholder(normalizedSourceType)}
            value={sampleText}
            onChange={(e) => setSampleText(e.target.value)}
            className="font-mono text-[13px] min-h-[120px]"
            spellCheck={false}
          />
          <Button variant="secondary" onClick={runPreview} disabled={sampleText.trim() === ''}>
            Run preview
          </Button>
          {previewError && <p className="text-xs text-red-500">{previewError}</p>}
          {previewResults && <MappingPreviewResults results={previewResults} />}
        </FormSection>
      )}

      <div className="flex gap-3">
        <Button onClick={save} loading={saving} disabled={!canSave} full>
          {isEdit ? 'Save changes' : 'Create mapping'}
        </Button>
        {isEdit && (
          <Button variant="destructive" onClick={remove} loading={deleting}>
            <Trash2 size={16} />
          </Button>
        )}
      </div>

      {error && <Toast message={error} onDismiss={() => setError('')} />}
      <UpgradePrompt
        open={!!limitMessage}
        onClose={() => setLimitMessage('')}
        message={limitMessage}
        title="Connector limit reached"
      />
    </div>
  );
}

const STATUS_LABEL: Record<string, string> = {
  emitted: 'Emitted',
  'dropped-required': 'Dropped (required field missing)',
  'no-name': 'No name (no `to: "name"` rule matched, and no nickname to fall back to)',
  'no-rules': 'No `rules`/`outputs` in this output',
  'filtered-allowlist': 'Filtered by spec.metricAllowlist',
  'skipped-non-finite': 'Skipped (non-finite value)',
  'unresolved-signal-ref': 'Targets a signal schema - resolved server-side, not shown here',
};

// Quiet by design (see Badge's own header comment) - a status word rather
// than a colored pill, since "this rule dropped the record" is normal
// mapping behaviour, not necessarily an error.
function statusColor(status: string): string {
  if (status === 'emitted') return 'text-emerald-600 dark:text-emerald-400';
  if (status === 'no-rules') return 'text-red-500';
  return 'text-amber-600 dark:text-amber-400';
}

function MappingPreviewResults({ results }: { results: PreviewRecordResult[] }) {
  const allOutputs = results.flatMap((r) => r.outputs);
  const emitted = allOutputs.filter((o) => o.status === 'emitted').length;

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm font-medium">
        {results.length} record{results.length === 1 ? '' : 's'} &rarr; {emitted} signal{emitted === 1 ? '' : 's'}{' '}
        emitted{allOutputs.length > emitted ? `, ${allOutputs.length - emitted} not emitted` : ''}
      </p>
      <div className="flex flex-col gap-2">
        {results.map((r) => (
          <div
            key={r.recordIndex}
            className="rounded-xl border border-black/[0.06] dark:border-white/[0.08] p-3 flex flex-col gap-2"
          >
            <span className="text-xs font-semibold text-black/40 dark:text-white/40">Record {r.recordIndex + 1}</span>
            {r.outputs.map((o) => (
              <div key={o.outputIndex} className="flex flex-col gap-1">
                {o.status !== 'emitted' ? (
                  <span className={`text-xs ${statusColor(o.status)}`}>
                    {STATUS_LABEL[o.status]}
                    {o.status === 'unresolved-signal-ref' && o.signalRef ? ` (#${o.signalRef})` : ''}
                  </span>
                ) : (
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge className="text-emerald-600 dark:text-emerald-400 border-emerald-600/30">
                      {o.signal!.name}
                    </Badge>
                    {o.signal!.value && <span className="text-sm font-mono">{o.signal!.value}</span>}
                    <span className="text-xs text-black/40 dark:text-white/40">
                      {o.signal!.at}
                      {o.signal!.atSynthesized ? ' (now — no `at` rule matched)' : ''}
                    </span>
                    {o.signal!.labels.map((l, i) => (
                      <Badge key={i}>{l}</Badge>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
